import React, { useRef, useState } from "react";
import { Form, Col } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { ReactSortable } from "react-sortablejs";
import { v4 as uuid } from "uuid";
import styled from "@emotion/styled";
import { getInputEl } from './getInputEl';
import { OperationList } from './operation-list'

// 表格控件 单元格内可放置其余表单控件
export const TableTD = ({
    formConfig,
    item,
    generate,
    activeKey,
    formItems,
    setFormItems,
    onClick
}) => {
    const [activeTd, setActiveTd] = useState('');

    /*
        拖动操作信息
        type: 新增类型 add.从组件库拖入 move.从已有组件拖入
        from: 来源索引
        to: 目标索引
    */
    const sortInfo = useRef({});

    // 单元格控件新增 1.从外部控件库加入 2.从单元格外部移入 3.从别的单元格移入 【注意移入后的顺序】
    const tdAddAction = (toTableKey, toR, toC, toIndex, newComp, oldList) => {
        const newList = updateTDItems('insert', oldList || formItems, toTableKey, toR, toC, newComp, toIndex);

        setFormItems(newList);
    }

    // 单元格内相互移动
    const tdMoveAction = (fromTableKey, fromR, fromC, toTableKey, toR, toC, addIndex, moveComp) => {
        // 移除操作需延迟，因setList与onAdd是同步回调，需等待nextTick
        setTimeout(() => {
            // 先移除
            const newList = updateTDItems('delete', formItems, fromTableKey, fromR, fromC, moveComp);
            // 再插入
            tdAddAction(toTableKey, toR, toC, addIndex, moveComp, newList);
        }, 0);
    }

    // 单元格内控件排序
    const tdItemsSortAction = (toTableKey, toR, toC, newComps) => {
        const newList = updateTDItems('replace', formItems, toTableKey, toR, toC, newComps);
        setFormItems(newList);
    }

    // sourtable-js 原生事件 排序新增元素回调 在onSetList之前触发
    const onAdd = (e) => {
        // 从左侧组件库拖入新增
        if (e.from.classList[0] === 'comp-source') {
            sortInfo.current = {
                type: 'add',
                from: 0,
                to: e.to.classList[e.to.classList.length - 1]
            };
        }
        // 从表格外部移入
        else if (e.from.classList[0] === 'comp-item') {
            sortInfo.current = {
                type: 'move',
                from: e.item.classList[e.item.classList.length - 1],
                to: e.to.classList[e.to.classList.length - 1]
            };
        }
        // 从单元格移入单元格
        else if (e.from.classList[0] === 'td-item' && e.to.classList[0] === 'td-item') {
            sortInfo.current = {
                type: 'td-move',
                from: e.item.classList[e.item.classList.length - 1],
                to: e.to.classList[e.to.classList.length - 1]
            };
        }
    };

    // sourtable-js 原生事件 排序变更回调 在onSetList之前触发
    const onSortUpdate = (e) => {
        if (e.from.className === e.to.className) {
            sortInfo.current = {
                type: 'td-sort',
                from: e.item.classList[e.item.classList.length - 1],
                to: e.to.classList[e.to.classList.length - 1]
            }
        }
    }

    // 单元格内控件变更
    const onSetList = (newlist) => {
        const { type, from, to } = sortInfo.current;

        // 从左侧组件库拖入新增
        if (type === 'add') {
            const [, toTbKey, toR, toC] = to.split('_');

            // 找到新增控件
            const addOne = newlist.find(el => !el.key);
            // 不可再拖入表格
            if (addOne.type === 'table') return;
            // 插入的索引值
            const addIndex = newlist.findIndex(el => !el.key);
            tdAddAction(toTbKey, Number(toR), Number(toC), addIndex, addOne);
        }
        // 从表格外部移入
        else if (type === 'move') {
            const [, toTbKey, toR, toC] = to.split('_');

            const fromKey = from.split('_');
            const moveOne = formItems.find(el => el.key === fromKey[1]);
            // 不可再拖入表格
            if (moveOne.type === 'table') return;
            const addIndex = newlist.findIndex(el => el.key === moveOne.key);
            tdAddAction(toTbKey, Number(toR), Number(toC), addIndex, moveOne);
        }
        // 从单元格移入单元格
        else if (type === 'td-move') {
            // 找到表格和单元格内的控件
            const [, fromTbKey, fromRi, fromCi, fromI] = from.split('_');
            const [, toTbKey, toR, toC] = to.split('_');

            const moveTable = formItems.find(el => el.key === fromTbKey);
            const moveOne = moveTable['children'][Number(fromRi)][Number(fromCi)]['children'][Number(fromI)];

            const addIndex = newlist.findIndex(el => el.key === moveOne.key);
            tdMoveAction(fromTbKey, Number(fromRi), Number(fromCi), toTbKey, Number(toR), Number(toC), addIndex, moveOne);
        }
        // 单元格内部控件排序
        else if (type === 'td-sort') {
            const [, toTbKey, toR, toC] = to.split('_');
            const toTable = formItems.find(el => el.key === toTbKey);
            const newComps = newlist.map((compItem, compIndex) => {
                return {
                    ...compItem,
                    formItemProps: {
                        ...compItem.formItemProps,
                        // 全新field name
                        name: `${toTable.formItemProps.name}_td_${toR}_${toC}_${compIndex}`,
                    }
                };
            });
            tdItemsSortAction(toTbKey, Number(toR), Number(toC), newComps);
        }
        // 处理完毕，清除此次操作信息
        sortInfo.current = {};
    };

    // 单元格内控件克隆
    const onItemCopy = (rI, cI, record) => {
        const newList = updateTDItems('append', formItems, item.key, rI, cI, [record], item.formItemProps.name);
        setFormItems(newList);
    }

    // 删除控件
    const onItemDelete = (rI, cI, record) => {
        const newList = updateTDItems('delete', formItems, item.key, rI, cI, record);
        setFormItems(newList);
    }

    /**
     * 显示合并单元格操作按钮 会冒泡 改动
     * */
    function onShowOperation(rIndex, cIndex) {
        const newKey = `${item.key}_${rIndex}_${cIndex}`;
        setActiveTd(newKey === activeTd ? '' : newKey);
    }

    return (
        <SortTable>
            <tbody>
                {(item.children || []).map((row, rIndex) => (
                    <tr key={rIndex}>
                        {row.map((col, cIndex) => {
                            if (col.merge) {
                                return null
                            }
                            return (
                                <td
                                    key={cIndex}
                                    valign="top"
                                    colSpan={col.colspan || 1}
                                    rowSpan={col.rowspan || 1}
                                    className={activeTd === `${item.key}_${rIndex}_${cIndex}` ? 'operation' : ''}
                                    onClick={() => onShowOperation(rIndex, cIndex)}
                                    onContextMenu={(e) => {
                                        if (!generate) return;
                                        e.preventDefault();
                                    }}
                                    onMouseDown={(e) => {
                                        if (!generate) return;
                                        if (e.button !== 2) return;
                                        onShowOperation(rIndex, cIndex);
                                    }}
                                >
                                    <ReactSortable
                                        className={`td-item ant-row td_${item.key}_${rIndex}_${cIndex}`}
                                        group="comp"
                                        animation={250}
                                        // 每个单元格内控件作为一个可拖动最小组件
                                        list={col.children}
                                        onAdd={onAdd}
                                        onUpdate={onSortUpdate}
                                        setList={onSetList}
                                        fallbackOnBody={true}
                                        disabled={!generate}
                                    >
                                        {(col.children || []).map(
                                            (compItem, comp_index) => (
                                                <Col
                                                    key={comp_index}
                                                    span={compItem.span || 24}
                                                    className={`td-comp-item ${activeKey === compItem.key ? 'active' : 'no-active'} comp_${item.key}_${rIndex}_${cIndex}_${comp_index}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onClick && onClick(compItem, {
                                                            pKey: item.key,
                                                            rIndex,
                                                            cIndex,
                                                            index: comp_index
                                                        })
                                                    }}
                                                >
                                                    <Form.Item
                                                        {...compItem.formItemProps}
                                                        {...(formConfig.labelAlign ? { labelAlign: formConfig.labelAlign } : {})}
                                                        {...(!compItem.showLabel ? { label: '' } : {})}
                                                        {...(!compItem.showLabel || formConfig.layout !== 'horizontal'
                                                            ? {
                                                                labelCol: undefined,
                                                                wrapperCol: undefined,
                                                            } : {})
                                                        }
                                                        {...(compItem.type === 'upload' ? {
                                                            getValueFromEvent: (e) => {
                                                                if (e instanceof Array) {
                                                                    return e.filter((file) => file.status !== 'error')
                                                                }
                                                                return (e && e.fileList.filter((file) => file.status !== 'error'))
                                                            },
                                                        } : {})}
                                                    >
                                                        {getInputEl(compItem)}
                                                    </Form.Item>
                                                    {
                                                        generate ? (
                                                            <>
                                                                <div
                                                                    className="icon-btn copy"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        onItemCopy(rIndex, cIndex, compItem)
                                                                    }}
                                                                >
                                                                    <CopyOutlined />
                                                                </div>
                                                                <div
                                                                    className="icon-btn delete"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        onItemDelete(rIndex, cIndex, compItem)
                                                                    }}
                                                                >
                                                                    <DeleteOutlined />
                                                                </div>
                                                            </>
                                                        ) : null
                                                    }
                                                </Col>
                                            )
                                        )}
                                    </ReactSortable>

                                    {
                                        generate ? (
                                            <OperationList
                                                item={item}
                                                rIndex={rIndex}
                                                cIndex={cIndex}
                                                formItems={formItems}
                                                setFormItems={setFormItems}
                                            />
                                        ) : null
                                    }
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
        </SortTable>
    )
};

// 匹配单元格内控件 进行操作 insert.插入 delete.移除 replace.替换 append.克隆追加
export const updateTDItems = (type, oldList, tableKey, rI, cI, compItem, extra) => oldList.map(el => {
    if (el.key !== tableKey) return el;
    return {
        ...el,
        children: el.children.map((tr, i_r) => {
            if (i_r !== rI) return tr;
            return tr.map((td, i_c) => {
                if (i_c !== cI) return td;
                switch (type) {
                    case 'insert':
                        const beforeItems = td.children.slice(0, extra);
                        const afterItems = td.children.slice(extra);
                        const toTable = oldList.find(el1 => el1.key === tableKey);

                        const allFormItemsName = td.children.map((cItem) => {
                            const formItemNameArr = (cItem.formItemProps.name || '').split('_');
                            if (formItemNameArr.length < 2) return 'custom';
                            const formItemName = formItemNameArr[formItemNameArr.length - 1];
                            return isNaN(formItemName) ? 'custom' : Number(formItemName);
                        })
                            .filter((cItem) => cItem !== 'custom');
                        const maxIndex = allFormItemsName.length > 0 ? Math.max.apply(null, allFormItemsName) : 0;

                        return {
                            ...td,
                            children: [
                                ...beforeItems,
                                {
                                    ...compItem,
                                    key: uuid(),
                                    formItemProps: {
                                        ...compItem.formItemProps,
                                        name: `${toTable.formItemProps.name}_td_${rI}_${cI}_${maxIndex + 1}`,
                                        labelCol: { span: 6 },
                                        wrapperCol: { span: 18 },
                                    },
                                    span: 24,
                                    showLabel: false
                                },
                                ...afterItems
                            ]
                        };

                    case 'delete':
                        return {
                            ...td,
                            children: td.children.filter(cItem => cItem.key !== compItem.key)
                        };

                    case 'replace':
                        return {
                            ...td,
                            children: compItem
                        };

                    // 追加 支持多个
                    case 'append':
                        return {
                            ...td,
                            children: [
                                ...td.children,
                                ...compItem.map(cItem => ({
                                    ...cItem,
                                    key: uuid()
                                }))
                            ].map((cItem, compI) => ({
                                ...cItem,
                                formItemProps: {
                                    ...cItem.formItemProps,
                                    name: `${extra}_td_${rI}_${cI}_${compI}`,
                                }
                            }))
                        };

                    default:
                        return td;
                }
            });
        })
    };
});

const SortTable = styled.table`
    width: 100%;
    table-layout: fixed;
    td {
        border: 1px solid #d0d0d0;
        position: relative;
        &.operation {
            border: 1px solid #2878ff;
            outline: 2px solid #2878ff;
            outline-offset: -1px;
            > .td-setting-btn {
                display: block;
            }
        }
        & > .td-item {
            min-height: 46px;
            /* margin: 4px; */
            & > .td-comp-item {
                position: relative;
                &.active {
                    background: rgba(40, 120, 255, 0.1);
                }
                &:hover > .icon-btn {
                    display: block;
                    &.copy {
                        background: #409eff;
                        color: #fff;
                    }
                    &.delete {
                        background: #f56c6c;
                        color: #fff;
                    }
                }
            }
        }
        & > .td-setting-btn {
            position: absolute;
            z-index: 3;
            bottom: 0px;
            right: 0;
            width: 22px;
            height: 22px;
            line-height: 22px;
            text-align: center;
            font-size: 12px;
            background: #2878ff;
            color: #fff;
            cursor: pointer;
            display: none;
        }
    }
`